import { clubPath } from '../state/club-session';
/**
 * News API Service
 * Conecta con el endpoint /api/news/units/{competitionUnitId} para obtener el feed
 * oficial de noticias, traspasos y sucesos de la competición.
 * Mock fallback: /mocks/news/news-list.json
 */
import { NewsResponse } from '../models/types';
import { apiSimulator } from './api-simulator.service';

const cache = new Map<string, { expiresAt: number; promise: Promise<NewsResponse>; data?: NewsResponse; pending: boolean }>();

export const newsApi = {
  clearCache(): void {
    cache.clear();
  },
  /**
   * GET /api/news/units/{competitionUnitId}
   * Obtiene las noticias oficiales de una unidad de competición (traspasos, fichajes, etc.).
   */
  async getNewsByUnit(
    competitionUnitId: string,
    params?: { cursor?: string; limit?: number },
    forceRefresh = false,
    onCached?: (response: NewsResponse) => void
  ): Promise<NewsResponse> {
    if (!competitionUnitId) {
      return { items: [], nextCursor: null };
    }

    let endpoint = clubPath('/news',competitionUnitId);
    const queryParts: string[] = [];
    if (params?.cursor) {
      queryParts.push(`cursor=${encodeURIComponent(params.cursor)}`);
    }
    if (params?.limit) {
      queryParts.push(`limit=${encodeURIComponent(params.limit.toString())}`);
    }
    if (queryParts.length > 0) {
      endpoint += `?${queryParts.join('&')}`;
    }

    const cached = cache.get(endpoint);
    if (cached?.data) onCached?.(cached.data);
    if (cached?.pending) return cached.promise;
    if (!forceRefresh && cached && cached.expiresAt > Date.now()) return cached.promise;

    const entry = { expiresAt: Infinity, data: cached?.data, pending: true, promise: Promise.resolve().then(() => apiSimulator.callWithMockFallback<NewsResponse>({
      method: 'GET',
      endpoint,
      service: 'news',
      mockName: 'news-list',
      transformMock: (mock) => {
        if (!mock || !Array.isArray(mock.items)) {
          return { items: [], nextCursor: null };
        }
        return {
          ...mock,
          items: mock.items.map((item: any) => ({
            ...item,
            competitionUnitId: competitionUnitId || item.competitionUnitId,
          })),
        };
      },
    })) };
    cache.set(endpoint, entry);
    entry.promise.then((response) => {
      entry.data = response;
      entry.pending = false;
      const midnight = new Date(Date.now());
      midnight.setHours(24, 0, 0, 0);
      entry.expiresAt = midnight.getTime();
    }, () => {
      if (cache.get(endpoint) === entry) {
        if (cached?.data) cache.set(endpoint, cached);
        else cache.delete(endpoint);
      }
    });
    return entry.promise;
  },
};
